import type {
    BotInformation,
    UserStatus,
    User as UserI,
    Channel as ChannelI,
    RelationshipStatus,
    FieldsUser,
    DataEditUser,
    LdapInfo,
    Presence,
} from "revolt-api";
import type { File } from "revolt-api";

import {
    makeAutoObservable,
    action,
    runInAction,
    computed,
    ObservableMap,
    observable,
} from "mobx";
import isEqual from "lodash.isequal";

import { U32_MAX, UserPermission } from "../permissions/definitions";
import { toNullable, Nullable } from "../util/null";
import { Client, FileArgs } from "..";
import { decodeTime } from "ulid";

export class User {
    client: Client;

    _id: string;
    username: string;
    discriminator: string;
    display_name?: string | null;

    ldap_info: Nullable<LdapInfo>;
    avatar: Nullable<File>;
    badges: Nullable<number>;
    status: Nullable<UserStatus>;
    relationship: Nullable<RelationshipStatus>;
    online: boolean;
    privileged: boolean;
    flags: Nullable<number>;
    bot: Nullable<BotInformation>;

    /**
     * Get timestamp when this user was created.
     */
    get createdAt() {
        return decodeTime(this._id);
    }

    get name() {
        return this.display_name || this.username;
    }

    get isMe() {
        return this.relationship === "User";
    }

    constructor(client: Client, data: UserI) {
        this.client = client;

        this._id = data._id;
        this.username = data.username;
        this.discriminator = data.discriminator;
        this.display_name = data.display_name;

        this.ldap_info = toNullable(data.ldap_info);
        this.avatar = toNullable(data.avatar);
        this.badges = toNullable(data.badges);
        this.status = toNullable(data.status);
        this.relationship = toNullable(data.relationship);
        this.online = data.online ?? false;
        this.privileged = data.privileged ?? false;
        this.flags = toNullable(data.flags);
        this.bot = toNullable(data.bot);

        makeAutoObservable(this, {
            _id: false,
            client: false,
            update: action,
            permission: computed,
        });
    }

    update(data: Partial<UserI>, clear: FieldsUser[] = []) {
        const apply = (key: string) => {
            // This code has been tested.
            if (
                // @ts-expect-error TODO: clean up types here
                typeof data[key] !== "undefined" &&
                // @ts-expect-error TODO: clean up types here
                !isEqual(this[key], data[key])
            ) {
                // @ts-expect-error TODO: clean up types here
                this[key] = data[key];

                if (key === "relationship") {
                    this.client.emit("user/relationship", this);
                }
            }
        };

        for (const entry of clear) {
            switch (entry) {
                case "Avatar":
                    this.avatar = null;
                    break;
                case "StatusText": {
                    if (this.status) {
                        this.status.text = undefined;
                    }
                    break;
                }
                case "DisplayName":
                    this.display_name = null;
                    break;
            }
        }

        apply("username");
        apply("discriminator");
        apply("display_name");
        apply("avatar");
        apply("badges");
        apply("status");
        apply("relationship");
        apply("online");
        apply("privileged");
        apply("flags");
        apply("bot");
        apply("ldap_info");
    }

    /**
     * Open a DM with a user
     * @returns DM Channel
     */
    async openDM() {
        let dm = [...this.client.channels.map.values()].find(
            (x) => x.channel_type === "DirectMessage" && x.recipient == this,
        );

        if (!dm) {
            const data = (await this.client.api.get(
                `/users/${this._id as ""}/dm`,
            )) as ChannelI;
            dm = await this.client.channels.fetch(data._id, data)!;
        }

        runInAction(() => {
            dm!.active = true;
        });

        return dm;
    }

    /**
     * Send a friend request to a user
     */
    async addFriend() {
        return await this.client.api.post(`/users/friend`, {
            username: this.username + "#" + this.discriminator,
        });
    }

    /**
     * Remove a user from the friend list
     */
    async removeFriend() {
        return await this.client.api.delete(`/users/${this._id as ""}/friend`);
    }

    /**
     * Block a user
     */
    async blockUser() {
        return await this.client.api.put(`/users/${this._id as ""}/block`);
    }

    /**
     * Unblock a user
     */
    async unblockUser() {
        return await this.client.api.delete(`/users/${this._id as ""}/block`);
    }

    async setPresence(presence: Presence) {
        return this.client.users.edit({
            status: {
                ...this.client.user?.status,
                presence,
            },
        });
    }

    /**
     * Fetch the profile of a user
     * @returns The profile of the user
     */
    async fetchProfile() {
        return await this.client.api.get(`/users/${this._id as ""}/profile`);
    }

    /**
     * Fetch the mutual connections of the current user and a target user
     * @returns The mutual connections of the current user and a target user
     */
    async fetchMutual() {
        return await this.client.api.get(`/users/${this._id as ""}/mutual`);
    }

    /**
     * Get the default avatar URL of a user
     */
    get defaultAvatarURL() {
        return `${this.client.apiURL}/users/${this._id}/default_avatar`;
    }

    /**
     * Get a pre-configured avatar URL of a user
     */
    get avatarURL() {
        return this.generateAvatarURL({ max_side: 256 });
    }

    /**
     * Get a pre-configured animated avatar URL of a user
     */
    get animatedAvatarURL() {
        return this.generateAvatarURL({ max_side: 256 }, true);
    }

    generateAvatarURL(...args: FileArgs) {
        return (
            this.client.generateFileURL(this.avatar ?? undefined, ...args) ??
            this.defaultAvatarURL
        );
    }

    get permission() {
        switch (this.relationship) {
            case "Friend":
            case "User":
                return U32_MAX;
            case "Blocked":
            case "BlockedOther":
                return UserPermission.Access;
        }
        // Allow to DM any user without friendship relationship
        return U32_MAX;
    }
}

export default class Users {
    map: ObservableMap<string, User>;
    client: Client;

    constructor(client: Client) {
        this.client = client;
        this.map = observable.map();
        this.createObj = this.createObj.bind(this);
        this.map.set(
            "00000000000000000000000000",
            new User(client, {
                _id: "00000000000000000000000000",
                username: "Revolt",
                discriminator: "0000",
                display_name: "Revolt", // typing issue
                relationship: "None",
                online: false,
            }),
        );

        makeAutoObservable(this);
    }

    get contactList() {
        const list: User[] = [];
        this.map.forEach((usr) => {
            if (!usr.isMe) {
                list.push(usr);
            }
        });
        return list.sort((a, b) => a.name.localeCompare(b.name));
    }

    get me() {
        return [...this.map.values()].find((usr) => usr.isMe);
    }

    /**
     * @deprecated
     * @use .map использовать методы свойства .map
     */
    values() {
        return this.map.values();
    }

    /**
     * @deprecated
     * @use .map использовать методы свойства .map
     */
    set(key: string, value: User) {
        return this.map.set(key, value);
    }

    /**
     * @deprecated
     * @use .map использовать методы свойства .map
     */
    get(key: string) {
        return this.map.get(key);
    }

    /**
     * @deprecated
     * @use .map использовать методы свойства .map
     */
    has(key: string) {
        return this.map.has(key);
    }

    $get(id: string, data?: UserI) {
        return runInAction(() => {
            const user = this.get(id)!;
            if (data) user.update(data);
            return user;
        });
    }

    /**
     * Fetch a user
     * @param id User ID
     * @returns User
     */
    async fetch(id: string, data?: UserI) {
        if (this.map.has(id)) return this.$get(id, data);
        const res =
            data ??
            ((await this.client.api.get(`/users/${id as ""}`)) as UserI);

        return this.createObj(res);
    }

    /**
     * Create a user object.
     * This is meant for internal use only.
     * @param data: User Data
     * @returns User
     */
    createObj(data: UserI) {
        if (this.map.has(data._id)) return this.$get(data._id, data);
        const user = new User(this.client, data);

        runInAction(() => {
            this.set(data._id, user);
        });

        this.client.emit("user/relationship", user);
        return user;
    }

    /**
     * Edit the current user
     * @param data User edit data object
     */
    async edit(data: DataEditUser) {
        const updated = (await this.client.api.patch(
            "/users/@me",
            data,
        )) as UserI;
        // Apply the response to the local user instance the same way the
        // user_update websocket path does; without this the UI keeps showing
        // the stale status until a refetch.
        if (updated && this.client.user) {
            this.client.users
                .get(this.client.user._id)
                ?.update(updated, data.remove ?? []);
        }
    }

    setPresence(presence: Presence) {
        return this.edit({
            status: {
                ...this.client.user?.status,
                presence,
            },
        });
    }

    /**
     * Change the username of the current user
     * @param username New username
     * @param password Current password
     */
    async changeUsername(username: string, password: string) {
        return await this.client.api.patch("/users/@me/username", {
            username,
            password,
        });
    }
}
